import React, { useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart, MapChart } from 'echarts/charts';
import {
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { Box, Paper, Typography } from '@mui/material';

registerECharts([
  MapChart,
  VisualMapComponent,
  BarChart,
  LineChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Props = {
  option: EChartsOption;
  title: string;
  source: string;
  range: string;
  onSelect?: (value: { product: string; date?: string; additive?: boolean }) => void;
  onClear?: () => void;
  height?: number;
};

/** Native ECharts runtime; only the pure chart-option builder is shared with standalone. */
export default function ForecastChart({ option, title, source, range, onSelect, onClear, height = 340 }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const select = useRef(onSelect);
  const clear = useRef(onClear);
  useEffect(() => {
    select.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    clear.current = onClear;
  }, [onClear]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event) => {
      const original = event.event?.event;
      select.current?.({
        product: event.name || event.seriesName || '',
        date: event.name,
        additive: !!(original?.ctrlKey || original?.metaKey || original?.shiftKey),
      });
    });
    chart.getZr().on('click', (event) => {
      if (!event.target) clear.current?.();
    });
    let resizeFrame: number | undefined;
    const observer = new ResizeObserver(() => {
      if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
      // Responsive grid transitions can briefly measure zero. A zero-sized geo
      // creates a singular transform in ECharts; resize only after layout settles.
      resizeFrame = requestAnimationFrame(() => {
        const bounds = element.current?.getBoundingClientRect();
        if (bounds && bounds.width > 0 && bounds.height > 0 && !chart.isDisposed()) chart.resize();
      });
    });
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
      chart.dispose();
      instance.current = undefined;
    };
  }, []);
  useEffect(() => {
    instance.current?.setOption(option, { notMerge: true });
  }, [option]);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', minWidth: 0, borderRadius: 0, boxShadow: 'none', border: 0 }}>
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${range}. ${onSelect ? 'Select a country to filter.' : ''}`}
        sx={{
          width: '100%',
          height: { xs: Math.min(height, 280), md: `clamp(300px, 32vw, ${height}px)` },
          minWidth: 0,
        }}
      />
      <Typography
        variant="caption"
        title={source}
        sx={(theme) => ({
          display: 'block',
          px: 0.75,
          pb: 0.75,
          fontSize: 10,
          color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#526164',
          overflowWrap: 'anywhere',
        })}
      >
        {range}
      </Typography>
    </Paper>
  );
}
